import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { LogOut, RefreshCw, BarChart3, Car, Inbox } from "lucide-react";
import AdminVehicles from "./admin/AdminVehicles";
import AdminAnalytics from "./admin/AdminAnalytics";

const SUB_KINDS = [
  { key: "quotes", label: "Quote Requests" },
  { key: "reservations", label: "Reservations" },
  { key: "finance", label: "Finance Enquiries" },
  { key: "fleet", label: "Fleet Enquiries" },
  { key: "imports", label: "Import Requests" },
  { key: "contacts", label: "Contact Messages" },
  { key: "partners", label: "Partner Enquiries" },
];

function AdminSubmissions() {
  const [summary, setSummary] = useState({});
  const [active, setActive] = useState("quotes");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const [s, r] = await Promise.all([
        api.get("/admin/summary"),
        api.get(`/admin/submissions/${active}`),
      ]);
      setSummary(s.data); setRows(r.data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const columns = rows[0] ? Object.keys(rows[0]).filter((k) => k !== "id") : [];

  return (
    <div data-testid="admin-submissions">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="overline text-[#C5A059] mb-2">Submissions</div>
          <div className="font-display text-3xl">Enquiries · All Channels</div>
        </div>
        <button onClick={load} className="btn-editorial" data-testid="refresh-btn"><RefreshCw size={13}/> Refresh</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-7 gap-3 mb-8">
        {SUB_KINDS.map((k) => (
          <button
            key={k.key}
            onClick={() => setActive(k.key)}
            data-testid={`tab-${k.key}`}
            className={`border p-4 text-left transition-colors duration-500 ${active === k.key ? "border-[#C5A059] bg-[#C5A059]/5" : "border-white/10 hover:border-white/30"}`}
          >
            <div className="text-[10px] tracking-[0.22em] uppercase text-white/50">{k.label}</div>
            <div className="font-display text-3xl mt-1">{summary[k.key] ?? 0}</div>
          </button>
        ))}
      </div>

      <div className="border border-white/10 overflow-x-auto">
        {loading ? (
          <div className="p-8 text-white/50 text-center">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-white/50 text-center" data-testid="empty-state">No submissions yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-white/[0.02]">
              <tr>{columns.map((c) => <th key={c} className="text-left p-4 text-[10px] uppercase tracking-[0.22em] text-white/50">{c.replace(/_/g," ")}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.id || i} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`row-${i}`}>
                  {columns.map((c) => (
                    <td key={c} className="p-4 text-white/80 align-top max-w-xs">
                      {typeof row[c] === "boolean" ? (row[c] ? "Yes" : "No") : String(row[c] ?? "")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const TABS = [
  { key: "analytics", label: "Analytics", icon: BarChart3 },
  { key: "vehicles", label: "Vehicles", icon: Car },
  { key: "submissions", label: "Submissions", icon: Inbox },
];

function AdminDashboardInner() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("analytics");
  const nav = useNavigate();

  return (
    <div data-testid="admin-dashboard" className="pt-28 pb-24">
      <div className="mx-auto max-w-[1500px] px-6 md:px-12">
        <div className="flex items-center justify-between mb-10">
          <div>
            <div className="overline text-[#C5A059] mb-2">Maklan · Admin</div>
            <div className="font-display text-4xl">Command Centre</div>
            <div className="text-white/50 text-sm mt-1">Signed in as {user?.email}</div>
          </div>
          <button onClick={async () => { await logout(); nav("/admin"); }} className="btn-editorial" data-testid="logout-btn">
            <LogOut size={13}/> Sign out
          </button>
        </div>

        {/* Top-level tab bar */}
        <div className="flex flex-wrap gap-2 border-b border-white/10 mb-10">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`admin-tab-${t.key}`}
                className={`inline-flex items-center gap-2 px-6 py-4 text-[11px] tracking-[0.28em] uppercase transition-colors duration-500 border-b-2 -mb-px ${
                  tab === t.key ? "border-[#C5A059] text-[#C5A059]" : "border-transparent text-white/60 hover:text-white"
                }`}
              >
                <Icon size={13}/> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "analytics" && <AdminAnalytics />}
        {tab === "vehicles" && <AdminVehicles />}
        {tab === "submissions" && <AdminSubmissions />}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, checking } = useAuth();
  if (checking) return <div className="pt-40 text-center text-white/60">…</div>;
  if (!user) return <Navigate to="/admin" replace />;
  return <AdminDashboardInner />;
}
